import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { Task, TaskEvent, TaskableType, TaskableTypeEnum } from '@hefe/models';
import {
  TasksApi,
  TasksSlice,
  currentUserSelector,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { userHasRole } from '@hefe/utils';
import { Button, Col, Row, Space, Steps, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import TasksAutoAssignmentWizardEventsComponent from '../tasks-auto-assignment-wizard-events/tasks-auto-assignment-wizard-events.component';
import TasksAutoAssignmentWizardModeComponent from '../tasks-auto-assignment-wizard-mode/tasks-auto-assignment-wizard-mode.component';
import TasksAutoAssignmentWizardOptionsComponent from '../tasks-auto-assignment-wizard-options/tasks-auto-assignment-wizard-options.component';
import TasksAutoAssignmentWizardPreviewComponent from '../tasks-auto-assignment-wizard-preview/tasks-auto-assignment-wizard-preview.component';
import TasksAutoAssignmentWizardUsersComponent from '../tasks-auto-assignment-wizard-users/tasks-auto-assignment-wizard-users.component';

interface TasksAutoAssignmentWizardProps {
  task: Task;
  events: TaskEvent[];
  type: TaskableTypeEnum;
  closeModal: () => void;
}

export default function TasksAutoAssignmentWizard(
  props: TasksAutoAssignmentWizardProps
) {
  const [currentStep, setCurrentStep] = useState(0);

  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);
  const activeTeamId = useSelector(userActiveBasketballCoachTeamId);
  const wizard = useSelector(TasksSlice.autoAssignmentWizardSelector);

  const [multiAssignToTask, { isLoading }] =
    TasksApi.useMultiAssignToTaskMutation();

  const stepKeys = useMemo(() => {
    const keys = [];

    if (
      userHasRole(AuthRoles.bbadmin, user) &&
      props.type === TaskableType.CLUB
    ) {
      keys.push('mode');
    }

    keys.push(...['events', 'users', 'options', 'preview']);

    return keys;
  }, [props.type, user]);

  const basketballTeamId = useMemo(() => {
    if (props.type === TaskableType.BASKETBALL_TEAM) {
      return activeTeamId ?? undefined;
    }
    return undefined;
  }, [activeTeamId, props.type]);

  const onClickBack = () => {
    setCurrentStep((current) => {
      if (current === 0) {
        return 0;
      }
      return current - 1;
    });
  };

  const onClickNext = () => {
    setCurrentStep((current) => {
      if (current === stepKeys.length - 1) {
        saveAssignments();
        return stepKeys.length - 1;
      }
      return current + 1;
    });
  };

  const saveAssignments = () => {
    const events = wizard.preview.map((preview) => {
      return {
        id: preview.id,
        assignable_ids: preview.assignments.map(
          (assignment) => assignment.value as number
        ),
      };
    });

    multiAssignToTask({
      taskId: props.task.id,
      request: {
        assignable_type: wizard.mode,
        basketball_team_id: basketballTeamId,
        events: events,
      },
    }).then(() => {
      props.closeModal();
      setCurrentStep(0);
      dispatch(TasksSlice.actions.resetWizard());
    });
  };

  const nextButtonDisabled = useMemo(() => {
    switch (stepKeys[currentStep]) {
      case 'events':
        return wizard.eventIds.length === 0;
      case 'users':
        return wizard.assignables.length === 0;
      case 'options':
        return (
          wizard.options.numberOfAssigneesPerEvent >
            (props.task.max_num_of_users ?? 99) ||
          wizard.options.numberOfAssigneesPerEvent <
            (props.task.min_num_of_users ?? 1)
        );
      default:
        return false;
    }
  }, [
    currentStep,
    props.task.max_num_of_users,
    props.task.min_num_of_users,
    stepKeys,
    wizard.assignables.length,
    wizard.eventIds.length,
    wizard.options.numberOfAssigneesPerEvent,
  ]);

  const renderContent = () => {
    switch (stepKeys[currentStep]) {
      case 'events':
        return (
          <TasksAutoAssignmentWizardEventsComponent events={props.events} />
        );
      case 'users':
        return (
          <TasksAutoAssignmentWizardUsersComponent
            task={props.task}
            basketballTeamId={basketballTeamId}
          />
        );
      case 'options':
        return <TasksAutoAssignmentWizardOptionsComponent task={props.task} />;
      case 'preview':
        return (
          <TasksAutoAssignmentWizardPreviewComponent
            task={props.task}
            basketballTeamId={basketballTeamId}
          />
        );
      case 'mode':
        return <TasksAutoAssignmentWizardModeComponent />;
      default:
        return (
          <TasksAutoAssignmentWizardEventsComponent events={props.events} />
        );
    }
  };

  return (
    <Row gutter={[16, 30]}>
      <Col span={24}>
        <Typography.Title level={3}>
          {t('tasks.auto_assignment.title')}
          &nbsp; (Experimental)
        </Typography.Title>
        <Steps
          size="small"
          current={currentStep}
          items={stepKeys.map((step) => {
            return {
              title: t(`tasks.auto_assignment.steps.${step}.title`),
            };
          })}
        />
      </Col>
      <Col span={24}>
        <Typography.Paragraph>
          {t(
            `tasks.auto_assignment.steps.${stepKeys[currentStep]}.description`
          )}
        </Typography.Paragraph>
        <div style={{ padding: 20 }}>{renderContent()}</div>
      </Col>
      <Col span={24}>
        <Space direction="horizontal">
          <Button disabled={currentStep === 0} onClick={() => onClickBack()}>
            {t('prev')}
          </Button>
          <Button
            disabled={nextButtonDisabled}
            onClick={() => onClickNext()}
            loading={isLoading}
          >
            {currentStep === stepKeys.length - 1 ? t('save') : t('next')}
          </Button>
        </Space>
      </Col>
    </Row>
  );
}
